// v5 blok 3: de vroegere "Custom"-pagina is "Technische info" geworden.
// Custom stenen (eigen kleuren/samenstellingen) worden NIET aangeboden — de
// fabriek vraagt te grote minimumafnames. Afwijkende maten kunnen wél, maar
// zonder configurator: dat loopt via contact. Per steen is er technische
// informatie + de downloadbare fabrikantfiche (PDF); meerdere fiches kunnen
// samen aangevinkt en geopend/geprint worden.
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useProducts, translateColor, translateTexture } from '../store/productData';
import { useLanguage } from '../store/LanguageContext';
import SEOHead from '../components/SEOHead';
import { FileText, Ruler, Mail, Download, CheckSquare, Square } from 'lucide-react';

export default function ProfessioneelPage() {
  const { t } = useLanguage();
  const { products } = useProducts();
  const stones = products;
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const toggle = (id: string) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  // Fiche-URL zit (nog) niet in het Product-type — lees defensief.
  const withFiche = (p: { id: string }): string | null =>
    (p as { fiche_url?: string | null; ficheUrl?: string | null }).fiche_url
    ?? (p as { ficheUrl?: string | null }).ficheUrl ?? null;

  // Open alle aangevinkte fiches — elk in een eigen tabblad; de browser
  // print/downloadt PDF's native. Stenen zonder fiche worden overgeslagen.
  const openSelected = () => {
    let opened = 0;
    for (const p of stones) {
      if (!selected.has(p.id)) continue;
      const url = withFiche(p);
      if (url) { window.open(url, '_blank'); opened++; }
    }
    if (opened === 0) alert('Geen van de geselecteerde stenen heeft al een technische fiche. De fiches worden per steen toegevoegd zodra de fabrikant ze aanlevert.');
  };

  const selectedWithFiche = [...selected].filter(id => {
    const p = stones.find(s => s.id === id);
    return p && withFiche(p);
  }).length;

  return (
    <div className="pt-20 md:pt-24">
      <SEOHead page="custom" />

      {/* Hero */}
      <section className="relative h-[160px] md:h-[190px] flex items-center overflow-hidden">
        <div className="absolute inset-0" style={{ backgroundImage: 'url(/products/linea-antraciet.jpg)', backgroundSize: 'cover', backgroundPosition: 'center' }} />
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/60 to-black/35" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <span className="text-[#c47a3a] text-sm font-semibold tracking-wider uppercase">Documentatie</span>
          <h1 className="text-2xl md:text-3xl font-bold text-white mt-1" style={{ fontFamily: 'Playfair Display' }}>
            Technische info
          </h1>
          <p className="text-gray-300 mt-3 max-w-2xl text-sm md:text-base">
            Per gevelsteen vindt u hier de technische informatie en de officiële
            technische fiche van de fabrikant (PDF). Vink meerdere stenen aan om
            de fiches samen te openen en te printen.
          </p>
        </div>
      </section>

      {/* Afwijkende maten — via contact, geen configurator */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-[#faf7f3] border border-[#c47a3a]/20 rounded-xl p-5 flex flex-col sm:flex-row sm:items-center gap-4">
          <Ruler size={28} className="text-[#c47a3a] flex-shrink-0" />
          <div className="flex-1">
            <h2 className="font-bold text-[#1a1a1a]">Afwijkende maten nodig?</h2>
            <p className="text-sm text-[#4a4a4a] mt-1">
              Eigen kleuren of samenstellingen bieden wij niet aan. Afwijkende maten
              van bestaande stenen zijn wél mogelijk — neem contact op en wij bekijken
              het met de fabrikant.
            </p>
          </div>
          <Link to="/contact" className="flex items-center gap-2 px-4 py-2.5 bg-[#c47a3a] hover:bg-[#a8643a] text-white rounded-lg text-sm font-bold transition-colors flex-shrink-0">
            <Mail size={15} /> Contacteer ons
          </Link>
        </div>
      </section>

      {/* Fiches per steen */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <h2 className="text-xl font-bold text-[#1a1a1a]" style={{ fontFamily: 'Playfair Display' }}>
            Technische fiches per steen
          </h2>
          <button
            onClick={openSelected}
            disabled={selected.size === 0}
            className="flex items-center gap-2 px-4 py-2 bg-[#c47a3a] hover:bg-[#a8643a] disabled:opacity-40 text-white rounded-lg text-sm font-bold transition-colors"
            title="Opent elke aangevinkte fiche in een eigen tabblad — vandaar samen printen of bewaren"
          >
            <Download size={15} /> Open {selected.size > 0 ? `${selectedWithFiche}/${selected.size} ` : ''}geselecteerde fiches
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {stones.map(p => {
            const fiche = withFiche(p);
            const isSel = selected.has(p.id);
            return (
              <div key={p.id} className={`bg-white border rounded-xl p-3 flex items-center gap-3 transition-colors ${isSel ? 'border-[#c47a3a] bg-[#c47a3a]/5' : 'border-gray-100'}`}>
                <button onClick={() => toggle(p.id)} className="flex-shrink-0 text-[#c47a3a]" title="Selecteer voor samen printen/downloaden">
                  {isSel ? <CheckSquare size={18} /> : <Square size={18} className="text-gray-300" />}
                </button>
                <img src={p.images[0]} alt={p.name} className="w-12 h-12 rounded object-cover flex-shrink-0" loading="lazy" />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-[#1a1a1a] truncate">{p.name}</p>
                  <p className="text-xs text-[#8a7e74] truncate">
                    {translateColor(p.color, t)} · {translateTexture(p.texture, t)} · {p.dimensions}
                  </p>
                </div>
                {fiche ? (
                  <a href={fiche} target="_blank" rel="noreferrer"
                    className="flex items-center gap-1 px-2 py-1.5 bg-[#faf7f3] hover:bg-[#c47a3a]/10 text-[#c47a3a] rounded-lg text-xs font-bold flex-shrink-0">
                    <FileText size={13} /> PDF
                  </a>
                ) : (
                  <span className="text-[10px] text-[#8a7e74] flex-shrink-0" title="De fabrikantfiche wordt toegevoegd zodra beschikbaar">volgt</span>
                )}
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
